import { Injectable, signal, inject, computed } from '@angular/core';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { SalesStats } from 'src/app/models/core/chart-interfaces';
import { AmountInput } from 'src/app/utils/currency-format';
import { EMPTY_CHANNEL_AMOUNTS, mapChannelAmounts } from 'src/app/utils/reports/channel-split';
import { mapEventKpis } from 'src/app/utils/reports/event-kpis.mapper';
import { buildSalesKpiBlock, KpiBlockVm } from 'src/app/utils/reports/kpi-block';
import { navigateToStat } from 'src/app/utils/reports/stat-navigation';
import { latestRequestTracker } from 'src/app/utils/reports/latest-request';
import { NGXLogger } from 'ngx-logger';
import { GlobalReportApiService } from './global-report-api.service';
import { ReportScopeService } from '../report-scope.service';
import { ReportForbiddenHandler } from '../report-forbidden.handler';

@Injectable({ providedIn: 'root' })
export class HeaderKpisService {
  private platform = inject(Platform);
  private router = inject(Router);
  private logger = inject(NGXLogger);
  private forbiddenHandler = inject(ReportForbiddenHandler);
  // El skeleton lo apaga solo la respuesta del último pedido (aunque sea de otro rol).
  private readonly kpisRequest = latestRequestTracker();

  readonly loading = signal(false);
  readonly eventName = signal<string>('');
  readonly salesStats = signal<SalesStats[]>([]);

  // Crudo de la API (puede ser string); kpiBlock lo formatea con formatAmount.
  readonly totalAmount = signal<AmountInput>(0);

  // Monto por canal (de `sales_report_table`, ya en número: la API los manda string).
  private readonly channelAmounts = signal(EMPTY_CHANNEL_AMOUNTS);

  /** Todo lo que muestra `app-report-kpi-block` en las 5 pestañas del evento. */
  readonly kpiBlock = computed<KpiBlockVm>(() =>
    buildSalesKpiBlock(this.salesStats(), this.totalAmount(), this.channelAmounts()),
  );

  private _isNative = signal(Capacitor.isNativePlatform());
  private _isIOS = signal(this.platform.is('ios'));
  readonly isIOSNative = computed(() => this._isNative() && this._isIOS());

  /**
   * Scope de lo último cargado: `(rol, evento)`, no el evento solo. El back
   * acota los reportes con el rol, así que el mismo evento visto con otro rol es
   * otro dato. `null` = nada cargado.
   */
  private loadedKey: string | null = null;
  private currentEventId: number | string | null = null;

  get eventId(): number | string | null {
    return this.currentEventId;
  }

  constructor(
    private globalReportApi: GlobalReportApiService,
    private reportScope: ReportScopeService,
  ) { }

  setEventId(eventId: number | string): void {
    if (this.currentEventId !== eventId) {
      this.currentEventId = eventId;
      this.loadedKey = null;
      this.eventName.set('');
      this.salesStats.set([]);
      this.totalAmount.set(0);
      this.channelAmounts.set(EMPTY_CHANNEL_AMOUNTS);
    }
  }

  loadIfNeeded(): void {
    if (this.loadedKey === this.cacheKey()) return;
    this.load();
  }

  reload(): void {
    this.loadedKey = null;
    this.load();
  }

  private cacheKey(): string {
    return this.reportScope.keyFor(this.currentEventId);
  }

  private load(): void {
    if (!this.currentEventId) return;

    this.loading.set(true);
    const isLatest = this.kpisRequest.start();
    const eventId = this.currentEventId;
    const key = this.cacheKey();
    this.loadedKey = key;
    // Una respuesta tardía de otro (rol, evento) no puede pisar el dato actual.
    const isStale = (): boolean => key !== this.cacheKey();

    this.globalReportApi
      .getKpis(this.currentEventId, 'report_my_sales')
      .subscribe({
        next: (res) => {
          if (!isLatest()) return;
          this.loading.set(false);
          if (isStale()) return;
          this.applyKpis(res);
        },
        error: (error: unknown) => {
          if (!isLatest()) return;
          this.loading.set(false);
          if (isStale()) return;
          // Permiso revocado: el handler saca al usuario del evento; no es un error de carga.
          if (this.forbiddenHandler.handle(error)) {
            this.loadedKey = null;
            return;
          }
          this.logger.error('Error cargando los KPIs del evento (Jefe/Admin)', { eventId, error });
          this.loadedKey = null;
          this.salesStats.set([]);
          // null se muestra "—": un error no se confunde con un evento sin ventas.
          this.totalAmount.set(null);
        },
      });

    this.globalReportApi
      .getKpis(this.currentEventId, 'sales_report_table')
      .subscribe({
        next: (res) => {
          if (isStale()) return;
          this.channelAmounts.set(mapChannelAmounts(res));
        },
        // El reparto es accesorio: si falla, la tarjeta queda sin filas pero el
        // resto del bloque sigue. Se invalida el cache para reintentar al volver a entrar.
        error: (error: unknown) => {
          if (isStale()) return;
          if (this.forbiddenHandler.handle(error)) {
            this.loadedKey = null;
            return;
          }
          this.logger.error('Error cargando el reparto por canal (Jefe/Admin)', { eventId, error });
          this.channelAmounts.set(EMPTY_CHANNEL_AMOUNTS);
          this.loadedKey = null;
        },
      });
  }

  private applyKpis(res: unknown): void {
    const kpis = mapEventKpis(res);
    this.eventName.set(kpis.eventName);
    this.totalAmount.set(kpis.totalAmount);
    this.salesStats.set(kpis.stats);
  }

  // Navegación al hacer click en una card del header. Depende solo del eventId,
  // por eso es uniforme para todos los tabs y vive acá (no se duplica por tab).
  navigateToStat(stat: { titleKey: string }): void {
    navigateToStat(this.router, this.currentEventId, stat.titleKey);
  }

  resetAllState(): void {
    this.eventName.set('');
    this.salesStats.set([]);
    this.totalAmount.set(0);
    this.channelAmounts.set(EMPTY_CHANNEL_AMOUNTS);
    this.loading.set(false);
    this.loadedKey = null;
    this.currentEventId = null;
  }
}
